<template>
  <div class="sidebar" :class="{ 'sidebar-mobile-open': mobileMenuOpen, 'sidebar-collapsed': authStore.sidebarCollapsed }">
    <!-- Logo -->
    <div class="sidebar-header">
      <div class="sidebar-header-content">
        <div class="logo">
          <img src="@/assets/logo.png" alt="QUALI-D" class="logo-icon" />
        </div>
        <div v-if="!authStore.sidebarCollapsed" class="meta-partner-badge">
          <img
            :src="themeStore.isDark
              ? 'https://quali-d.com/resources/Meta_Business_Partners_inline_lockup_negative_primary_RGB.png'
              : 'https://quali-d.com/resources/Meta_Business_Partners_inline_lockup_positive_primary_RGB.png'"
            alt="Meta Business Partner"
            class="meta-partner-logo"
          />
        </div>
      </div>
      <button class="sidebar-toggle-btn" @click="authStore.toggleSidebar()" :title="authStore.sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path v-if="!authStore.sidebarCollapsed" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          <path v-else d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
        </svg>
      </button>
    </div>

    <!-- Subscription Expired Overlay -->
    <div v-if="authStore.subscriptionExpired && !isAgent" class="subscription-expired-overlay">
      <div class="expired-icon">
        <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
        </svg>
      </div>
      <h3 class="expired-title">Subscription Expired</h3>
      <p class="expired-message">Your subscription has ended. Please renew to continue using QUALI-D.</p>
      <button @click="goToRenew" class="renew-btn">Renew Now</button>
    </div>

    <!-- Navigation -->
    <nav class="sidebar-nav" :class="{ 'nav-disabled': authStore.subscriptionExpired && !isAgent }">

      <!-- Marketing Agent: restricted to Templates, Broadcast, Customers only -->
      <template v-if="isMarketingAgent">
        <div class="nav-section">
          <h3 class="nav-section-title">Marketing</h3>
          <ul class="nav-list">
            <li class="nav-item">
              <router-link to="/whatsapp-templates" class="nav-link" active-class="active" data-tooltip="WA Templates">
                <span class="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"/>
                  </svg>
                </span>
                <span class="nav-text">Templates</span>
              </router-link>
            </li>
            <li class="nav-item">
              <router-link to="/whatsapp-broadcast" class="nav-link" active-class="active" data-tooltip="WA Broadcast">
                <span class="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12,8H4A2,2 0 0,0 2,10V14A2,2 0 0,0 4,16H5V20A1,1 0 0,0 6,21H8A1,1 0 0,0 9,20V16H12L17,20V4L12,8M21.5,12C21.5,13.71 20.54,15.26 19,16.04V7.96C20.54,8.74 21.5,10.29 21.5,12Z"/>
                  </svg>
                </span>
                <span class="nav-text">Broadcast</span>
              </router-link>
            </li>
            <li v-if="agentCanSeePhone" class="nav-item">
              <router-link to="/customers" class="nav-link" active-class="active" data-tooltip="Customers">
                <span class="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                  </svg>
                </span>
                <span class="nav-text">Customers</span>
              </router-link>
            </li>
            <li v-if="!isAgent" class="nav-item">
              <router-link to="/customer-tags" class="nav-link" active-class="active" data-tooltip="Customer Tags">
                <span class="nav-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/>
                  </svg>
                </span>
                <span class="nav-text">Customer Tags</span>
                <span class="nav-badge new">New</span>
              </router-link>
            </li>
          </ul>
        </div>
      </template>

      <!-- Full navigation for all other roles -->
      <template v-else>
      <div class="nav-section">
        <h3 class="nav-section-title">Main</h3>
        <ul class="nav-list">
          <li class="nav-item">
            <router-link :to="isMarketingAgent ? '/whatsapp-templates' : (isAgent ? '/agent-dashboard' : '/')" class="nav-link" active-class="active" data-tooltip="Dashboard">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
                </svg>
              </span>
              <span class="nav-text">Dashboard</span>
            </router-link>
          </li>
          <!-- AI Analysis (hidden)
          <li class="nav-item" v-if="!isAgent">
            <router-link to="/ai-analysis" class="nav-link" data-tooltip="AI Analysis">
              <span class="nav-icon">
                <img src="@/assets/ai-platform-svgrepo-com.svg" width="18" height="18" alt="" />
              </span>
              <span class="nav-text">AI Analysis</span>
              <span class="nav-badge ai">Smart</span>
            </router-link>
          </li>
          -->
        </ul>
      </div>

      <!-- WhatsApp Section -->
      <div class="nav-section">
        <h3 class="nav-section-title">WhatsApp</h3>
        <ul class="nav-list">
          <li class="nav-item" v-if="!isMarketingAgent">
            <router-link to="/whatsapp" class="nav-link" data-tooltip="WhatsApp">
              <span class="nav-icon">
                <img src="@/assets/whatsapp-svgrepo-com.svg" width="18" height="18" alt="" />
              </span>
              <span class="nav-text">WhatsApp</span>
              <span v-if="whatsappUnreadCount > 0" class="nav-badge danger">{{ whatsappUnreadCount }}</span>
              <span v-else class="nav-badge">Active</span>
            </router-link>
          </li>
          <!-- WhatsApp Broadcast - Conditional Access Based on Plan or Agent Permission -->
          <li class="nav-item" v-if="isMarketingAgent || (!isAgent && authStore.canAccessBroadcast()) || (isAgent && !isMarketingAgent && authStore.agentBroadcastPermission)">
            <router-link to="/whatsapp-broadcast" class="nav-link" data-tooltip="WA Broadcast">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,8H4A2,2 0 0,0 2,10V14A2,2 0 0,0 4,16H5V20A1,1 0 0,0 6,21H8A1,1 0 0,0 9,20V16H12L17,20V4L12,8M21.5,12C21.5,13.71 20.54,15.26 19,16.04V7.96C20.54,8.74 21.5,10.29 21.5,12Z"/>
                </svg>
              </span>
              <span class="nav-text">Broadcast</span>
            </router-link>
          </li>
          <!-- WhatsApp Broadcast - Restricted Message for Limited Plans -->
          <li class="nav-item restricted-item" v-if="!isAgent && !authStore.canAccessBroadcast() && authStore.subscription">
            <div class="nav-link restricted-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,8H4A2,2 0 0,0 2,10V14A2,2 0 0,0 4,16H5V20A1,1 0 0,0 6,21H8A1,1 0 0,0 9,20V16H12L17,20V4L12,8M21.5,12C21.5,13.71 20.54,15.26 19,16.04V7.96C20.54,8.74 21.5,10.29 21.5,12Z"/>
                </svg>
              </span>
              <span class="nav-text restricted-text">Broadcast</span>
              <span class="nav-badge locked">Locked</span>
            </div>
            <div class="restricted-message">
              Upgrade To Pro or Starter Plan To Use WhatsApp Marketing Campaigns
            </div>
          </li>
          <li class="nav-item" v-if="!isAgent || isMarketingAgent">
            <router-link to="/whatsapp-templates" class="nav-link" data-tooltip="WA Templates">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M14,17H7V15H14M17,13H7V11H17M17,9H7V7H17M19,3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5C21,3.89 20.1,3 19,3Z"/>
                </svg>
              </span>
              <span class="nav-text">Templates</span>
            </router-link>
          </li>
          <li class="nav-item flows-spotlight-item" v-if="!isAgent && isAdmin">
            <router-link to="/custom-flows" class="nav-link flows-spotlight-link" :class="{ 'flow-mode-active': authStore.customFlowMode }">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 104.745 104.745" fill="currentColor">
                  <rect x="42.5" y="17.812" width="18.745" height="18.745"/>
                  <rect y="68.188" width="18.745" height="18.745"/>
                  <rect x="42" y="68.188" width="18.745" height="18.745"/>
                  <rect x="86" y="68.188" width="18.745" height="18.745"/>
                  <path d="M98.088,57.084c0.743,0.65,1.062,1.731,0.98,3.306l1.998,0.104c0.117-2.227-0.428-3.834-1.661-4.914c-2.966-2.597-9.188-1.428-18.61,0.338c-7.848,1.473-17.52,3.252-28.497,3.472V40.812h-2V59.39c-10.978-0.22-20.648-1.999-28.496-3.472c-9.419-1.767-15.644-2.935-18.61-0.338c-1.234,1.08-1.778,2.688-1.661,4.914l1.998-0.104c-0.083-1.574,0.237-2.655,0.98-3.306c2.234-1.955,8.716-0.739,16.924,0.801c7.892,1.479,17.604,3.277,28.688,3.511v0.046c0.399,0,0.782-0.02,1.177-0.023c0.396,0.004,0.779,0.023,1.178,0.023v-0.046c11.084-0.233,20.797-2.031,28.688-3.511C89.371,56.345,95.854,55.129,98.088,57.084z"/>
                </svg>
              </span>
              <span class="nav-text">Flows</span>
              <span v-if="authStore.customFlowMode" class="nav-badge flows-badge-live">Live</span>
              <span v-else class="nav-badge flows-badge-star">✦ Flow</span>
            </router-link>
          </li>
        </ul>
      </div>

      <!-- Messenger & Telegram Section -->
      <div class="nav-section" v-if="isAdmin">
        <h3 class="nav-section-title">Messaging</h3>
        <ul class="nav-list">
          <li class="nav-item">
            <router-link to="/messenger" class="nav-link" data-tooltip="Messenger">
              <span class="nav-icon">
                <img src="@/assets/messenger-logo.svg" width="18" height="18" alt="" />
              </span>
              <span class="nav-text">Messenger</span>
              <span v-if="messengerUnreadCount > 0" class="nav-badge danger">{{ messengerUnreadCount }}</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/telegram" class="nav-link" data-tooltip="Telegram">
              <span class="nav-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="18" height="18" style="border-radius:4px">
                  <defs>
                    <linearGradient id="tsb-a" x1=".667" x2=".417" y1=".167" y2=".75"><stop offset="0" stop-color="#37aee2"/><stop offset="1" stop-color="#1e96c8"/></linearGradient>
                    <linearGradient id="tsb-b" x1=".66" x2=".851" y1=".437" y2=".802"><stop offset="0" stop-color="#eff7fc"/><stop offset="1" stop-color="#fff"/></linearGradient>
                  </defs>
                  <circle cx="120" cy="120" r="120" fill="url(#tsb-a)"/>
                  <path fill="#c8daea" d="M98 175c-3.888 0-3.227-1.468-4.568-5.17L82 132.207 170 80"/>
                  <path fill="#a9c9dd" d="M98 175c3 0 4.325-1.372 6-3l16-15.558-19.958-12.035"/>
                  <path fill="url(#tsb-b)" d="M100.04 144.41l48.36 35.729c5.519 3.045 9.501 1.468 10.876-5.123l19.685-92.763c2.015-8.08-3.08-11.746-8.36-9.349l-115.59 44.571c-7.89 3.165-7.843 7.567-1.438 9.528l29.663 9.259 68.673-43.325c3.242-1.966 6.218-.91 3.776 1.258"/>
                </svg>
              </span>
              <span class="nav-text">Telegram</span>
              <span v-if="telegramUnreadCount > 0" class="nav-badge danger">{{ telegramUnreadCount }}</span>
            </router-link>
          </li>
        </ul>
      </div>

      <!-- Communication Section -->
      <div class="nav-section" v-if="!isMarketingAgent">
        <h3 class="nav-section-title">Communication</h3>
        <ul class="nav-list">
          <li class="nav-item" v-if="!isAgent">
            <router-link to="/agents" class="nav-link" data-tooltip="Agents">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M5 9.5C5 7.01472 7.01472 5 9.5 5C11.9853 5 14 7.01472 14 9.5C14 11.9853 11.9853 14 9.5 14C7.01472 14 5 11.9853 5 9.5Z"/>
                  <path d="M14.3675 12.0632C14.322 12.1494 14.3413 12.2569 14.4196 12.3149C15.0012 12.7454 15.7209 13 16.5 13C18.433 13 20 11.433 20 9.5C20 7.567 18.433 6 16.5 6C15.7209 6 15.0012 6.2546 14.4196 6.68513C14.3413 6.74313 14.322 6.85058 14.3675 6.93679C14.7714 7.70219 15 8.5744 15 9.5C15 10.4256 14.7714 11.2978 14.3675 12.0632Z"/>
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M4.64115 15.6993C5.87351 15.1644 7.49045 15 9.49995 15C11.5112 15 13.1293 15.1647 14.3621 15.7008C15.705 16.2847 16.5212 17.2793 16.949 18.6836C17.1495 19.3418 16.6551 20 15.9738 20H3.02801C2.34589 20 1.85045 19.3408 2.05157 18.6814C2.47994 17.2769 3.29738 16.2826 4.64115 15.6993Z"/>
                  <path d="M14.8185 14.0364C14.4045 14.0621 14.3802 14.6183 14.7606 14.7837C15.803 15.237 16.5879 15.9043 17.1508 16.756C17.6127 17.4549 18.33 18 19.1677 18H20.9483C21.6555 18 22.1715 17.2973 21.9227 16.6108C21.9084 16.5713 21.8935 16.5321 21.8781 16.4932C21.5357 15.6286 20.9488 14.9921 20.0798 14.5864C19.2639 14.2055 18.2425 14.0483 17.0392 14.0008L17.0194 14H16.9997C16.2909 14 15.5506 13.9909 14.8185 14.0364Z"/>
                </svg>
              </span>
              <span class="nav-text">Agents</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="!isAgent">
            <router-link to="/departments" class="nav-link" data-tooltip="Departments">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                </svg>
              </span>
              <span class="nav-text">Departments</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/messages" class="nav-link" data-tooltip="Team Messaging">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 2H4c-1.1 0-1.99.9-1.99 2L2 22l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z"/>
                </svg>
              </span>
              <span class="nav-text">Team Messaging</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="!isAgent">
            <router-link to="/escalations" class="nav-link" data-tooltip="Escalations">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                </svg>
              </span>
              <span class="nav-text">Escalations</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section">
        <h3 class="nav-section-title">Productivity</h3>
        <ul class="nav-list">
          <li class="nav-item" v-if="!isMarketingAgent">
            <router-link to="/todo" class="nav-link" data-tooltip="ToDo">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </span>
              <span class="nav-text">ToDo</span>
            </router-link>
          </li>
          <li v-if="!isAgent || agentCanSeePhone" class="nav-item">
            <router-link to="/customers" class="nav-link" data-tooltip="Customers">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M 9.5 0 C 8.1252105 0 7 1.1252105 7 2.5 C 7 3.0633617 7.1940199 3.5807403 7.5117188 4 L 7.5 4 C 6.1252115 4 5 5.12521 5 6.5 L 5 10.5 C 5 11.702941 5.8636264 12.715051 7 12.949219 L 7 18.5 C 7 19.328427 7.6715729 20 8.5 20 L 10.5 20 C 11.328427 20 12 19.328427 12 18.5 L 12 12.949219 L 12.001953 12.949219 C 13.13828 12.715051 14 11.702941 14 10.5 L 14 6.5 C 14 5.12521 12.874789 4 11.5 4 L 11.488281 4 C 11.805981 3.5807403 12 3.0633617 12 2.5 C 12 1.1252105 10.87479 0 9.5 0 z M 4.5 1 C 4.285138 1.0000001 4.0801864 1.0362656 3.8808594 1.0878906 C 3.8543284 1.0950956 3.8289194 1.1032681 3.8027344 1.1113281 C 3.6122454 1.1674598 3.4326019 1.2442769 3.2636719 1.3417969 C 2.5137879 1.7746902 2 2.5766257 2 3.5 C 2 4.0633617 2.1940148 4.5807403 2.5117188 5 L 2.5 5 C 1.125211 5 0 6.12521 0 7.5 L 0 10.5 C -1.4802974e-016 11.702941 0.86190784 12.715051 1.9980469 12.949219 L 2 12.949219 L 2 16.5 C 2 17.328427 2.671573 18 3.5 18 L 5 18 L 6 18 L 6 17 L 5 17 L 5 14.910156 L 5 13.5 C 5 13.223858 4.776142 13 4.5 13 C 4.223858 13 4 13.223858 4 13.5 L 4 17 L 3.5 17 C 3.223858 17 3 16.776142 3 16.5 L 3 8.5 C 3 8.2238576 2.776142 8 2.5 8 C 2.223858 8 2 8.2238576 2 8.5 L 2 11.910156 C 1.416196 11.705514 1 11.157682 1 10.5 L 1 7.5 C 1 6.66565 1.66565 6 2.5 6 L 2.5117188 6 C 3.0640037 6 3.5117188 5.5522848 3.5117188 5 C 3.5111458 4.782732 3.440444 4.5712218 3.28125 4.3574219 C 3.122056 4.1436221 3 3.8417718 3 3.5 C 3 2.6656506 3.66565 2 4.5 2 C 4.788118 2 5.0528871 2.0814481 5.2050781 2.1738281 C 5.3572691 2.2662078 5.4473025 2.2925337 5.5390625 2.2929688 C 5.8152055 2.2929688 6.0390625 2.0691111 6.0390625 1.7929688 C 6.0390215 1.6415092 5.9671356 1.5075681 5.8847656 1.4394531 C 5.8023906 1.3713371 5.7722744 1.3560969 5.7402344 1.3417969 C 5.3733144 1.128618 4.952744 1 4.5 1 z M 9.5 1 C 10.33435 1 11 1.6656505 11 2.5 C 11 2.8417718 10.877896 3.1436223 10.71875 3.3574219 C 10.559605 3.5712214 10.488843 3.7827376 10.488281 4 C 10.488281 4.5522847 10.935996 5 11.488281 5 L 11.5 5 C 12.33435 5 13 5.66565 13 6.5 L 13 10.5 C 13 11.157682 12.583804 11.705514 12 11.910156 L 12 7.5 C 12 7.2238576 11.776142 7 11.5 7 C 11.223858 7 11 7.2238576 11 7.5 L 11 18.5 C 11 18.776142 10.776142 19 10.5 19 L 10 19 L 10 13.5 C 10 13.223858 9.7761424 13 9.5 13 C 9.2238576 13 9 13.223858 9 13.5 L 9 19 L 8.5 19 C 8.2238576 19 8 18.776142 8 18.5 L 8 7.5 C 8 7.2238576 7.7761424 7 7.5 7 C 7.2238576 7 7 7.2238576 7 7.5 L 7 11.910156 C 6.4161964 11.705514 6 11.157682 6 10.5 L 6 6.5 C 6 5.66565 6.6656505 5 7.5 5 L 7.5117188 5 C 8.0640035 5 8.5117188 4.5522847 8.5117188 4 C 8.5111567 3.7827376 8.4403749 3.5712202 8.28125 3.3574219 C 8.1221251 3.1436236 8 2.8417718 8 2.5 C 8 1.6656505 8.6656505 1 9.5 1 z M 14.5 1 C 14.047256 1 13.626686 1.128618 13.259766 1.3417969 C 13.227726 1.3560969 13.197609 1.3713371 13.115234 1.4394531 C 13.032864 1.5075681 12.960978 1.6415091 12.960938 1.7929688 C 12.960938 2.0691111 13.184795 2.2929688 13.460938 2.2929688 C 13.552698 2.2925337 13.642731 2.2662078 13.794922 2.1738281 C 13.947113 2.0814481 14.211882 2 14.5 2 C 15.33435 2 16 2.6656505 16 3.5 C 16 3.8417718 15.877944 4.1436221 15.71875 4.3574219 C 15.559556 4.5712218 15.488854 4.782732 15.488281 5 C 15.488281 5.5522847 15.935996 6 16.488281 6 L 16.5 6 C 17.33435 6 18 6.66565 18 7.5 L 18 10.5 C 18 11.157682 17.583804 11.705514 17 11.910156 L 17 8.5 C 17 8.2238576 16.776142 8 16.5 8 C 16.223858 8 16 8.2238576 16 8.5 L 16 16.5 C 16 16.776142 15.776142 17 15.5 17 L 15 17 L 15 13.5 C 15 13.223858 14.776142 13 14.5 13 C 14.223858 13 14 13.223858 14 13.5 L 14 14.910156 L 14 17 L 13 17 L 13 18 L 14 18 L 15.5 18 C 16.328427 18 17 17.328427 17 16.5 L 17 12.949219 L 17.001953 12.949219 C 18.138092 12.715051 19 11.702941 19 10.5 L 19 7.5 C 19 6.12521 17.874789 5 16.5 5 L 16.488281 5 C 16.805985 4.5807403 17 4.0633617 17 3.5 C 17 2.5766256 16.486212 1.7746902 15.736328 1.3417969 C 15.567398 1.2442766 15.387755 1.1674598 15.197266 1.1113281 C 15.171081 1.1032638 15.145672 1.0950959 15.119141 1.0878906 C 14.919814 1.0362653 14.714862 1 14.5 1 z"/>
                </svg>
              </span>
              <span class="nav-text">Customers</span>
            </router-link>
          </li>
          <li v-if="!isAgent" class="nav-item">
            <router-link to="/customer-tags" class="nav-link" data-tooltip="Customer Tags">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21.41 11.58l-9-9C12.05 2.22 11.55 2 11 2H4c-1.1 0-2 .9-2 2v7c0 .55.22 1.05.59 1.42l9 9c.36.36.86.58 1.41.58.55 0 1.05-.22 1.41-.59l7-7c.37-.36.59-.86.59-1.41 0-.55-.23-1.06-.59-1.42zM5.5 7C4.67 7 4 6.33 4 5.5S4.67 4 5.5 4 7 4.67 7 5.5 6.33 7 5.5 7z"/>
                </svg>
              </span>
              <span class="nav-text">Customer Tags</span>
              <span class="nav-badge new">New</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="!isAgent">
            <router-link to="/customer-satisfaction" class="nav-link" data-tooltip="Customer Satisfaction">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.5-9c.83 0 1.5-.67 1.5-1.5S16.33 8 15.5 8 14 8.67 14 9.5s.67 1.5 1.5 1.5zm-7 0c.83 0 1.5-.67 1.5-1.5S9.33 8 8.5 8 7 8.67 7 9.5 7.67 11 8.5 11zm3.5 6.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
                </svg>
              </span>
              <span class="nav-text">Customer Satisfaction</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="!isMarketingAgent">
            <router-link to="/leads" class="nav-link" data-tooltip="Leads">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z"/>
                </svg>
              </span>
              <span class="nav-text">Leads</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="!isAgent">
            <router-link to="/products-services" class="nav-link" data-tooltip="Products & Services">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.54,10.67l-.54.27-.54-.27-8-4s0,0,0,0h0l8-4.5a1,1,0,0,1,1,0l8,4.5h0s0,0,0,0l-4,2a.24.24,0,0,0-.09-.06l-4-2a1,1,0,0,0-.9,1.78l2.77,1.39ZM3,7.56a.06.06,0,0,1,0,0v9a1,1,0,0,0,.51.87l8,4.49v-10l-.5-.25Zm18,0-4,2V14a1,1,0,0,1-2,0V10.56l-2.5,1.25v10l8-4.49A1,1,0,0,0,21,16.5v-9A.06.06,0,0,1,21,7.56Z"/>
                </svg>
              </span>
              <span class="nav-text">Products/Services</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="authStore.appointmentMode">
            <router-link to="/appointments" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19,3H18V1H16V3H8V1H6V3H5A2,2 0 0,0 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V5A2,2 0 0,0 19,3M19,19H5V8H19V19M5,6V5H19V6H5Z"/>
                </svg>
              </span>
              <span class="nav-text">Appointments</span>
              <span class="nav-badge active">Active</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section" v-if="isAdmin">
        <h3 class="nav-section-title">Restaurant Mode</h3>
        <ul class="nav-list">
          <li class="nav-item">
            <router-link to="/restaurant-dashboard" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1"/>
                  <rect x="14" y="3" width="7" height="7" rx="1"/>
                  <rect x="14" y="14" width="7" height="7" rx="1"/>
                  <rect x="3" y="14" width="7" height="7" rx="1"/>
                </svg>
              </span>
              <span class="nav-text">Restaurant Dashboard</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/restaurant-settings" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"/>
                </svg>
              </span>
              <span class="nav-text">Restaurant Settings</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/restaurant-categories" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z"/>
                </svg>
              </span>
              <span class="nav-text">Menu Categories</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/restaurant-menu-items" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>
                </svg>
              </span>
              <span class="nav-text">Menu Items</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/restaurant-orders" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21,16.5C21,16.88 20.79,17.21 20.47,17.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L3.53,17.38C3.21,17.21 3,16.88 3,16.5V7.5C3,7.12 3.21,6.79 3.53,6.62L11.43,2.18C11.59,2.06 11.79,2 12,2C12.21,2 12.41,2.06 12.57,2.18L20.47,6.62C20.79,6.79 21,7.12 21,7.5V16.5M12,4.15L5,8.09V15.91L12,19.85L19,15.91V8.09L12,4.15Z"/>
                </svg>
              </span>
              <span class="nav-text">Orders</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/restaurant-riders" class="nav-link">
              <span class="nav-icon">🛵</span>
              <span class="nav-text">Riders</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section" v-if="!isAgent">
        <h3 class="nav-section-title">Management</h3>
        <ul class="nav-list">
          <li class="nav-item" v-if="isAdmin">
            <router-link to="/admin/financial" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6,16.5L3,19.44V11H6M11,14.66L9.43,13.32L8,14.64V7H11M16,13L13,16V3H16M18.81,12.81L17,11H22V16L20.21,14.21L13,21.36L9.53,18.34L5.75,22H3L9.47,15.66L13,18.64"/>
                </svg>
              </span>
              <span class="nav-text">Administration</span>
              <span class="nav-badge danger">Admin</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="isAdmin">
            <router-link to="/admin/whatsapp-accounts" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.498 14.382c-.301-.15-1.767-.867-2.04-.966-.273-.101-.473-.15-.673.15-.197.295-.771.964-.944 1.162-.175.195-.349.21-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.496.1-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.195 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.21-.57-.345m-5.446 7.443h-.016c-1.77 0-3.524-.48-5.055-1.38l-.36-.214-3.75.975 1.005-3.645-.239-.375a9.869 9.869 0 0 1-1.516-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/>
                </svg>
              </span>
              <span class="nav-text">WA Accounts</span>
              <span class="nav-badge danger">Admin</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="isAdmin">
            <router-link to="/admin/active-subscriptions" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M16,11C16,9.89 15.1,9 14,9H12V7H16V5H12V3H10V5H8V7H10V9H8C6.9,9 6,9.89 6,11V15C6,16.11 6.9,17 8,17H10V19H6V21H10V23H12V21H16V19H12V17H14C15.1,17 16,16.11 16,15V11M14,15H8V11H14V15Z"/>
                </svg>
              </span>
              <span class="nav-text">Active Subscriptions</span>
              <span class="nav-badge danger">Admin</span>
            </router-link>
          </li>
          <li class="nav-item" v-if="isAdmin">
            <router-link to="/admin/whatsapp-provisioning" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M17,11H15V9A3,3 0 0,0 12,6A3,3 0 0,0 9,9H7A5,5 0 0,1 12,4A5,5 0 0,1 17,9V11M17,13V15H15V13H17M7,13H9V15H7V13M7,11V9H9V11H7Z"/>
                </svg>
              </span>
              <span class="nav-text">WA Provisioning</span>
              <span class="nav-badge danger">Admin</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/payments" class="nav-link">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20,8H4V6C4,4.89 4.89,4 6,4H18A2,2 0 0,1 20,6V8M4,10V18A2,2 0 0,0 6,20H18A2,2 0 0,0 20,18V10H4M16,13.5A1.5,1.5 0 0,1 14.5,12A1.5,1.5 0 0,1 16,10.5A1.5,1.5 0 0,1 17.5,12A1.5,1.5 0 0,1 16,13.5Z"/>
                </svg>
              </span>
              <span class="nav-text">Payments & Billing</span>
            </router-link>
          </li>
          <li class="nav-item dropdown-item" :class="{ expanded: settingsExpanded }">
            <div class="nav-link dropdown-trigger" @click="toggleSettings">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.82,11.69,4.82,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/>
                </svg>
              </span>
              <span class="nav-text">Settings</span>
              <span class="dropdown-arrow" :class="{ rotated: settingsExpanded }">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                </svg>
              </span>
            </div>
            <ul class="dropdown-menu" v-show="settingsExpanded">
              <li class="dropdown-nav-item">
                <router-link to="/company-profile" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Company Profile</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/quick-replies" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.71 17.99A1.02 1.02 0 0 1 11 18H5c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2h14c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2h-6.01c-.26 0-.52.11-.71.29l-2.56 2.7zm8.58-13.29l.71-.71-1.41-1.41-.71.71 1.41 1.41z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Quick Replies</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/company-leads" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-4h3v4H4zM13 12.5h3.5l-1-4.5h-2.5v4.5zM12 13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0-8c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Company Leads</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/whatsapp-settings" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.485 3.488z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">WhatsApp Settings</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/whatsapp-bsp-onboarding" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.485 3.488z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">BSP Connect WhatsApp</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/whatsapp-profile" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">WhatsApp Profile</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/zoho-integration" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2l3.09 6.26L22 9l-5.09.74L12 16l-4.91-6.26L2 9l6.91-1.74L12 2zm0 2.5L9.5 8.5 4 9.5l5.5.5L12 13.5l2.5-3.5L20 9.5l-5.5-1L12 4.5zM7 18h10v2H7v-2z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Zoho CRM</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/odoo-integration" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Odoo CRM</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/meta-integration" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Meta CAPI</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/settings/google" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <img src="@/assets/google-color.svg" style="width:14px;height:14px" alt="Google" />
                  </span>
                  <span class="dropdown-nav-text">Google Integration</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/settings/microsoft" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <img src="@/assets/microsoft-color.svg" style="width:14px;height:14px" alt="Microsoft" />
                  </span>
                  <span class="dropdown-nav-text">Microsoft Integration</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <router-link to="/settings/security" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Security</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item" v-if="isAdmin">
                <router-link to="/settings/messenger" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.919 1.382 5.53 3.557 7.255V22l3.328-1.83A10.7 10.7 0 0012 20.486c5.523 0 10-4.144 10-9.243S17.523 2 12 2zm1.083 12.453L10.93 12.19l-4.194 2.263 4.61-4.9 2.216 2.263 4.144-2.263-4.623 4.9z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Messenger</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item" v-if="isAdmin">
                <router-link to="/settings/telegram" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" width="14" height="14" style="border-radius:3px">
                      <defs>
                        <linearGradient id="tsb-c" x1=".667" x2=".417" y1=".167" y2=".75"><stop offset="0" stop-color="#37aee2"/><stop offset="1" stop-color="#1e96c8"/></linearGradient>
                        <linearGradient id="tsb-d" x1=".66" x2=".851" y1=".437" y2=".802"><stop offset="0" stop-color="#eff7fc"/><stop offset="1" stop-color="#fff"/></linearGradient>
                      </defs>
                      <circle cx="120" cy="120" r="120" fill="url(#tsb-c)"/>
                      <path fill="#c8daea" d="M98 175c-3.888 0-3.227-1.468-4.568-5.17L82 132.207 170 80"/>
                      <path fill="#a9c9dd" d="M98 175c3 0 4.325-1.372 6-3l16-15.558-19.958-12.035"/>
                      <path fill="url(#tsb-d)" d="M100.04 144.41l48.36 35.729c5.519 3.045 9.501 1.468 10.876-5.123l19.685-92.763c2.015-8.08-3.08-11.746-8.36-9.349l-115.59 44.571c-7.89 3.165-7.843 7.567-1.438 9.528l29.663 9.259 68.673-43.325c3.242-1.966 6.218-.91 3.776 1.258"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Telegram</span>
                </router-link>
              </li>
              <li class="dropdown-nav-item">
                <a @click.prevent="openChangePasswordModal" class="dropdown-nav-link" href="#">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">Change Password</span>
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </div>

      <!-- Ad Pilot Section (admin only) -->
      <div class="nav-section" v-if="isAdmin">
        <h3 class="nav-section-title">Ad Pilot</h3>
        <ul class="nav-list">
          <li class="nav-item">
            <router-link to="/ad-pilot" class="nav-link" active-class="active" data-tooltip="Ad Pilot Dashboard">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M13.5 2c-5.621 0-10.211 4.443-10.475 10h-3.025l5 6.625 5-6.625h-2.975c.257-3.351 3.06-6 6.475-6 3.584 0 6.5 2.916 6.5 6.5s-2.916 6.5-6.5 6.5c-1.863 0-3.542-.793-4.728-2.053l-2.427 3.216c1.877 1.754 4.389 2.837 7.155 2.837 5.79 0 10.5-4.71 10.5-10.5s-4.71-10.5-10.5-10.5z"/></svg>
              </span>
              <span class="nav-text">Dashboard</span>
              <span class="nav-badge ai">Beta</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/ad-pilot/campaigns" class="nav-link" active-class="active" data-tooltip="Campaigns">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>
              </span>
              <span class="nav-text">Campaigns</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/ad-pilot/ai" class="nav-link" active-class="active" data-tooltip="AI Pilot">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M21 10.12h-6.78l2.74-2.82c-2.73-2.7-7.15-2.8-9.88-.1-2.73 2.71-2.73 7.08 0 9.79 2.73 2.71 7.15 2.71 9.88 0C18.32 15.65 19 14.08 19 12.1h2c0 1.98-.88 4.55-2.64 6.29-3.51 3.48-9.21 3.48-12.72 0-3.5-3.47-3.53-9.11-.02-12.58 3.51-3.47 9.14-3.47 12.65 0L21 3v7.12zM12.5 8v4.25l3.5 2.08-.72 1.21L11 13V8h1.5z"/></svg>
              </span>
              <span class="nav-text">AI Pilot</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/ad-pilot/media" class="nav-link" active-class="active" data-tooltip="Media Library">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>
              </span>
              <span class="nav-text">Media Library</span>
            </router-link>
          </li>
          <li class="nav-item">
            <router-link to="/ad-pilot/settings" class="nav-link" active-class="active" data-tooltip="Ad Pilot Settings">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.82,11.69,4.82,12s0.02,0.64,0.07,0.94l-2.03,1.58c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z"/></svg>
              </span>
              <span class="nav-text">Settings</span>
            </router-link>
          </li>
        </ul>
      </div>

      <div class="nav-section" v-if="!isAgent">
        <h3 class="nav-section-title">Help & Resources</h3>
        <ul class="nav-list">
          <li class="nav-item dropdown-item" :class="{ expanded: tutorialsExpanded }">
            <div class="nav-link dropdown-trigger" @click="toggleTutorials">
              <span class="nav-icon">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M11,7V13H13V7H11M11,15V17H13V15H11Z"/>
                </svg>
              </span>
              <span class="nav-text">Tutorials</span>
              <span class="dropdown-arrow" :class="{ rotated: tutorialsExpanded }">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                </svg>
              </span>
            </div>
            <ul class="dropdown-menu" v-show="tutorialsExpanded">
              <li class="dropdown-nav-item">
                <a href="https://www.youtube.com/watch?v=RT4Rm66WGco" target="_blank" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 16.5l6-4.5-6-4.5v9zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">How To Link Your Whatsapp</span>
                </a>
              </li>
              <li class="dropdown-nav-item">
                <a href="https://www.youtube.com/watch?v=fJo6MfGu__c" target="_blank" class="dropdown-nav-link">
                  <span class="dropdown-nav-icon">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M10 16.5l6-4.5-6-4.5v9zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                    </svg>
                  </span>
                  <span class="dropdown-nav-text">How To Add Products & Services</span>
                </a>
              </li>
            </ul>
          </li>
        </ul>
      </div>
      </template>
    </nav>

    <!-- Plan Section -->
    <div class="plan-section">
      <div class="plan-card">
        <div class="plan-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6,2L2,8L12,22L22,8L18,2H6M6.5,4H10.5L11.5,6H8L6.5,4M13.5,4H17.5L16,6H12.5L13.5,4M7.5,8H10.5L12,12L9,8H7.5M11,8H13L12,10L11,8M14.5,8H16.5L15,8L12,12L14.5,8Z"/>
          </svg>
        </div>
        <div class="plan-info">
          <div class="plan-name">{{ userPlan }}</div>
          <div class="plan-label">Current Plan</div>
        </div>
      </div>
    </div>

    <!-- Logout Section -->
    <div class="logout-section">
      <button @click="handleLogout" class="logout-btn">
        <span class="nav-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.59L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
          </svg>
        </span>
        <span class="nav-text">Logout</span>
      </button>
    </div>

    <!-- Change Password Modal -->
    <ChangePasswordModal
      v-if="showChangePasswordModal"
      @close="closeChangePasswordModal"
    />
  </div>
</template>

<script>
import { authStore } from '@/store/auth'
import { themeStore } from '@/store/theme'
import { DashboardService } from '@/services/dashboard'
import ChangePasswordModal from './ChangePasswordModal.vue'
import { clearFCMTokenFromBackend } from '@/services/firebase'

export default {
  name: 'Sidebar', // eslint-disable-line vue/multi-word-component-names
  components: {
    ChangePasswordModal
  },
  data () {
    return {
      mobileMenuOpen: false,
      settingsExpanded: false,
      tutorialsExpanded: false,
      authStore, // Make authStore available to template
      themeStore, // Make themeStore available to template
      whatsappUnreadCount: 0,
      messengerUnreadCount: 0,
      telegramUnreadCount: 0,
      unreadCountInterval: null,
      agentName: '',
      agentRole: '',
      showChangePasswordModal: false,
      permissionsKey: 0
    }
  },
  computed: {
    isAgent () {
      return !!localStorage.getItem('agent_token')
    },
    userPlan () {
      if (this.isAgent) {
        return this.agentRole || 'Agent'
      }
      return (authStore.user && authStore.user.plan) || 'No Plan'
    },
    isAdmin () {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      return user.role_id === 1
    },
    isMarketingAgent () {
      const agentToken = localStorage.getItem('agent_token')
      if (!agentToken) return false
      const agentData = JSON.parse(localStorage.getItem('agent_data') || '{}')
      return agentData.agent_role_name === 'Marketing' || parseInt(agentData.agent_role_id) === 3
    },
    agentCanSeePhone () {
      const canSee = this.permissionsKey >= 0 // reactive dep on permissionsKey; always true
      if (!this.isAgent) return canSee
      const d = JSON.parse(localStorage.getItem('agent_data') || '{}')
      return canSee && (d.show_customer_phone ?? 1) === 1
    }
  },
  watch: {
    // Only update sidebar state when mode actually changes, not on route changes
    'authStore.restaurantMode' () {
      // Mode changed, preserve scroll position
      this.preserveScrollOnNextTick()
    },
    'authStore.appointmentMode' () {
      // Mode changed, preserve scroll position
      this.preserveScrollOnNextTick()
    },
    $route (to) {
      // Auto-collapse on /whatsapp, /messenger and /telegram, restore on other routes
      const autoCollapse = to.path === '/whatsapp' || to.path === '/messenger' || to.path === '/telegram'
      if (autoCollapse && !authStore.sidebarCollapsed) {
        authStore.sidebarCollapsed = true
        document.documentElement.style.setProperty('--sidebar-width', '64px')
        document.body.classList.add('sidebar-collapsed')
      } else if (!autoCollapse && authStore.sidebarCollapsed && localStorage.getItem('sidebar-collapsed') !== 'true') {
        authStore.sidebarCollapsed = false
        document.documentElement.style.setProperty('--sidebar-width', '280px')
        document.body.classList.remove('sidebar-collapsed')
      }
    }
  },
  mounted () {
    // Initialize sidebar CSS variable and auto-collapse on /whatsapp and /messenger
    const onAutoCollapse = this.$route?.path === '/whatsapp' || this.$route?.path === '/messenger' || this.$route?.path === '/telegram'
    if (onAutoCollapse && !authStore.sidebarCollapsed) {
      authStore.sidebarCollapsed = true
    }
    document.documentElement.style.setProperty(
      '--sidebar-width',
      authStore.sidebarCollapsed ? '64px' : '280px'
    )
    document.body.classList.toggle('sidebar-collapsed', authStore.sidebarCollapsed)

    window.addEventListener('agent-permissions-updated', this.onAgentPermissionsUpdated)

    // Load agent data if in agent mode
    console.log('🔵 Sidebar mounted with isAgent prop:', this.isAgent)
    if (this.isAgent) {
      const agentData = JSON.parse(localStorage.getItem('agent_data') || '{}')
      this.agentName = agentData.name || 'Agent'
      this.agentRole = agentData.agent_role_name || 'Agent'
      console.log('🔵 Sidebar: Agent mode ENABLED, hiding non-agent items')
      // Fetch agent broadcast permission
      this.loadAgentBroadcastPermission()
    } else {
      console.log('🔴 Sidebar: Agent mode DISABLED, showing all items')
    }

    // Close mobile menu when clicking outside
    document.addEventListener('click', this.closeMobileMenuOnClickOutside)

    // Listen for custom event to toggle mobile menu
    window.addEventListener('toggleMobileMenu', this.handleMobileMenuToggle)

    // Load initial unread counts (only for owners, not agents)
    if (!this.isAgent) {
      this.fetchUnreadCount()
      this.fetchMessengerUnreadCount()
      this.fetchTelegramUnreadCount()

      // Poll for unread counts every 30 seconds
      this.unreadCountInterval = setInterval(() => {
        this.fetchUnreadCount()
        this.fetchMessengerUnreadCount()
        this.fetchTelegramUnreadCount()
      }, 30000)
    }

    // Preserve scroll position on route changes
    this.$nextTick(() => {
      const navElement = this.$el.querySelector('.sidebar-nav')
      if (navElement) {
        // Restore scroll position if saved
        const savedScrollPosition = sessionStorage.getItem('sidebar-scroll-position')
        if (savedScrollPosition) {
          navElement.scrollTop = parseInt(savedScrollPosition, 10)
        }

        // Save scroll position on scroll
        navElement.addEventListener('scroll', this.handleNavScroll)
      }
    })
  },
  beforeUnmount () {
    document.removeEventListener('click', this.closeMobileMenuOnClickOutside)
    window.removeEventListener('toggleMobileMenu', this.handleMobileMenuToggle)
    window.removeEventListener('agent-permissions-updated', this.onAgentPermissionsUpdated)

    // Clear polling interval
    if (this.unreadCountInterval) {
      clearInterval(this.unreadCountInterval)
    }

    // Clean up scroll listener
    const navElement = this.$el?.querySelector('.sidebar-nav')
    if (navElement) {
      navElement.removeEventListener('scroll', this.handleNavScroll)
    }
  },
  methods: {
    onAgentPermissionsUpdated () {
      this.permissionsKey++
    },
    handleNavScroll (event) {
      // Save scroll position to sessionStorage
      sessionStorage.setItem('sidebar-scroll-position', event.target.scrollTop)
    },
    preserveScrollOnNextTick () {
      // When mode changes, preserve scroll position
      const navElement = this.$el?.querySelector('.sidebar-nav')
      if (navElement) {
        const currentScroll = navElement.scrollTop
        this.$nextTick(() => {
          const updatedNavElement = this.$el?.querySelector('.sidebar-nav')
          if (updatedNavElement) {
            updatedNavElement.scrollTop = currentScroll
          }
        })
      }
    },
    async loadAgentBroadcastPermission () {
      try {
        const token = localStorage.getItem('agent_token')
        if (!token) return
        const res = await fetch('https://api.quali-d.com/whatsapp/broadcast/agent-permission', {
          headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' }
        })
        if (res.ok) {
          const data = await res.json()
          authStore.agentBroadcastPermission = data.allow_agent_broadcast || false
        }
      } catch (e) {
        console.warn('Could not load agent broadcast permission', e)
      }
    },

    async fetchUnreadCount () {
      try {
        const result = await DashboardService.getWhatsAppUnreadCount()
        if (result.success) {
          this.whatsappUnreadCount = result.unreadConversations || 0
        }
      } catch (error) {
        console.error('Error fetching WhatsApp unread count:', error)
      }
    },
    async fetchMessengerUnreadCount () {
      try {
        const result = await DashboardService.getMessengerUnreadCount()
        if (result.success) {
          this.messengerUnreadCount = result.unreadCount || 0
        }
      } catch (error) {
        console.error('Error fetching Messenger unread count:', error)
      }
    },
    async fetchTelegramUnreadCount () {
      try {
        const result = await DashboardService.getTelegramUnreadCount()
        if (result.success) {
          this.telegramUnreadCount = result.unread_count || 0
        }
      } catch (error) {
        console.error('Error fetching Telegram unread count:', error)
      }
    },
    handleMobileMenuToggle () {
      this.mobileMenuOpen = !this.mobileMenuOpen
    },
    closeMobileMenuOnClickOutside (event) {
      if (window.innerWidth <= 768 && this.mobileMenuOpen) {
        const sidebar = this.$el
        const hamburger = document.querySelector('.menu-toggle')
        if (sidebar && !sidebar.contains(event.target) && !hamburger?.contains(event.target)) {
          this.mobileMenuOpen = false
        }
      }
    },
    toggleSettings () {
      if (authStore.sidebarCollapsed) {
        authStore.toggleSidebar()
        this.$nextTick(() => { this.settingsExpanded = true })
      } else {
        this.settingsExpanded = !this.settingsExpanded
      }
    },
    toggleTutorials () {
      if (authStore.sidebarCollapsed) {
        authStore.toggleSidebar()
        this.$nextTick(() => { this.tutorialsExpanded = true })
      } else {
        this.tutorialsExpanded = !this.tutorialsExpanded
      }
    },
    goToRenew () {
      this.$router.push('/renew-subscription')
    },
    openChangePasswordModal () {
      this.showChangePasswordModal = true
    },
    closeChangePasswordModal () {
      this.showChangePasswordModal = false
    },
    async handleLogout () {
      if (this.isAgent) {
        console.log('Agent logout clicked from sidebar - clearing agent data...')

        const agentToken = localStorage.getItem('agent_token')
        if (agentToken) await clearFCMTokenFromBackend(agentToken)

        localStorage.removeItem('agent_token')
        localStorage.removeItem('agent_data')
        localStorage.removeItem('auth_token')

        this.$router.push('/agent-login')
      } else {
        console.log('Owner logout clicked from sidebar - clearing user data...')

        const authToken = localStorage.getItem('auth_token')
        if (authToken) await clearFCMTokenFromBackend(authToken)

        localStorage.removeItem('auth_token')
        localStorage.removeItem('user_data')

        authStore.logout()

        this.$router.push('/login')
      }
    }
  }
}
</script>

<style scoped>
.sidebar {
  width: 280px;
  height: 100vh;
  background: var(--bg-sidebar);
  border-right: 1px solid var(--border-primary);
  display: flex;
  flex-direction: column;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1000;
  transition: background-color 0.3s ease;
}

.sidebar-header {
  padding: 24px 20px;
  border-bottom: 1px solid var(--border-primary);
}

.meta-partner-badge {
  margin-top: 4px;
}

.meta-partner-logo {
  display: block;
  height: 32px;
  width: auto;
  opacity: 0.85;
}

.logo {
  display: flex;
  align-items: center;
  gap: 12px;
}

.logo-icon {
  height: 32px;
  width: auto;
}

.logo-text {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
}

.sidebar-nav {
  flex: 1;
  padding: 20px 0;
  overflow-y: auto;
}

/* Custom Scrollbar Styling */
.sidebar-nav::-webkit-scrollbar {
  width: 6px;
}

.sidebar-nav::-webkit-scrollbar-track {
  background: var(--bg-secondary);
  border-radius: 3px;
}

.sidebar-nav::-webkit-scrollbar-thumb {
  background: var(--text-tertiary);
  border-radius: 3px;
  transition: background 0.2s;
}

.sidebar-nav::-webkit-scrollbar-thumb:hover {
  background: var(--text-secondary);
}

.nav-section {
  margin-bottom: 32px;
}

.nav-section-title {
  font-size: 11px;
  font-weight: 600;
  color: var(--sidebar-section-title);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 20px 12px;
}

.nav-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.nav-item {
  margin: 0;
}

.nav-link {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  color: var(--sidebar-nav-text);
  text-decoration: none;
  transition: all 0.2s;
  position: relative;
}

.nav-link:hover {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.nav-item.active .nav-link,
.nav-link.active,
.nav-link.router-link-active {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
  border-right: 3px solid #3b82f6;
}

.nav-icon {
  font-size: 18px;
  width: 20px;
  display: flex;
  justify-content: center;
}

.nav-text {
  font-size: 14px;
  font-weight: 500;
}

.nav-badge {
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 600;
  background: #10b981;
  color: white;
}

.nav-badge.new {
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 40%, #c4b5fd 60%, #6366f1 100%);
  background-size: 250% 100%;
  animation: badge-shimmer 2.2s linear infinite;
  font-weight: 700;
  letter-spacing: 0.04em;
  font-size: 10px;
}

@keyframes badge-shimmer {
  0%   { background-position: 100% 0; }
  100% { background-position: -100% 0; }
}

.nav-badge.active {
  background: #10b981;
  animation: pulse 2s infinite;
}

.flow-mode-active {
  border-left: 3px solid #10b981 !important;
  padding-left: calc(16px - 3px) !important;
}

/* WhatsApp Flows — spotlight item */
.flows-spotlight-link {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.08), rgba(59, 130, 246, 0.06));
  border-left: 2px solid rgba(139, 92, 246, 0.5);
  padding-left: calc(16px - 2px);
}

.flows-spotlight-link::after {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 60%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.07),
    transparent
  );
  animation: flows-shimmer 3s ease-in-out infinite;
  pointer-events: none;
}

@keyframes flows-shimmer {
  0%   { left: -100%; }
  50%  { left: 150%; }
  100% { left: 150%; }
}

.flows-spotlight-link:hover {
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.15), rgba(59, 130, 246, 0.12)) !important;
}

.nav-badge.flows-badge-star {
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  animation: pulse 2s infinite;
  font-size: 10px;
  letter-spacing: 0.3px;
}

.nav-badge.flows-badge-live {
  background: #10b981;
  animation: pulse 2s infinite;
}

.nav-badge.danger {
  background: #ef4444;
  animation: pulse 2s infinite;
}

.nav-badge.ai {
  background: linear-gradient(135deg, #667eea, #764ba2);
  animation: pulse 2s infinite;
}

.nav-badge.locked {
  background: #f59e0b;
  color: white;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}

/* Restricted Item Styles */
.restricted-item {
  position: relative;
}

.restricted-link {
  cursor: not-allowed;
  opacity: 0.6;
  background: var(--bg-secondary);
}

.restricted-link:hover {
  background: var(--bg-secondary);
  color: var(--text-secondary);
}

.restricted-text {
  font-weight: 500;
}

.restricted-message {
  font-size: 11px;
  color: #f59e0b;
  padding: 4px 20px 8px 52px;
  line-height: 1.4;
  font-weight: 500;
  background: rgba(245, 158, 11, 0.1);
  margin-top: -8px;
  border-left: 3px solid #f59e0b;
}

/* Dropdown Styles */
.dropdown-item {
  position: relative;
}

.dropdown-trigger {
  cursor: pointer;
  position: relative;
}

.dropdown-arrow {
  margin-left: auto;
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
  opacity: 0.7;
}

.dropdown-arrow.rotated {
  transform: rotate(180deg);
}

.dropdown-menu {
  list-style: none;
  margin: 0;
  padding: 0;
  background: var(--bg-secondary) !important;
  border: 1px solid var(--border-primary);
  border-left: 2px solid rgba(59, 130, 246, 0.3);
  margin-left: 20px;
  margin-top: 4px;
  border-radius: 0 8px 8px 0;
  overflow: hidden;
  animation: slideDown 0.2s ease-out;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    max-height: 200px;
    transform: translateY(0);
  }
}

.dropdown-nav-item {
  margin: 0;
}

.dropdown-nav-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  color: var(--text-secondary) !important;
  background: transparent !important;
  text-decoration: none;
  transition: all 0.2s;
  font-size: 13px;
  position: relative;
}

.dropdown-nav-link:hover {
  background: var(--bg-primary) !important;
  color: var(--text-primary) !important;
  padding-left: 20px;
}

.dropdown-nav-link.router-link-active {
  background: rgba(59, 130, 246, 0.15) !important;
  color: #3b82f6 !important;
  border-right: 2px solid #3b82f6;
}

.dropdown-nav-icon {
  font-size: 14px;
  width: 16px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.dropdown-nav-icon svg {
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.dropdown-nav-link:hover .dropdown-nav-icon svg {
  opacity: 1;
}

.dropdown-nav-text {
  font-size: 13px;
  font-weight: 500;
}

/* Expanded state styles */
.dropdown-item.expanded .dropdown-trigger {
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.upgrade-card {
  margin: 20px;
  padding: 20px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 16px;
  text-align: center;
  color: white;
}

.upgrade-icon {
  font-size: 32px;
  margin-bottom: 8px;
}

.upgrade-card h4 {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
}

.upgrade-card p {
  margin: 0 0 16px;
  font-size: 13px;
  opacity: 0.9;
}

.upgrade-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.upgrade-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Plan Section */
.plan-section {
  margin: 20px;
  margin-bottom: 12px;
}

.plan-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  color: var(--text-primary);
  padding: 14px 16px;
  border-radius: 8px;
}

.plan-icon {
  font-size: 18px;
  opacity: 0.8;
  display: flex;
  align-items: center;
  justify-content: center;
}

.plan-icon svg {
  color: var(--text-primary);
  opacity: 0.9;
}

.plan-info {
  flex: 1;
}

.plan-name {
  font-size: 14px;
  font-weight: 600;
  line-height: 1.2;
  margin-bottom: 2px;
}

.plan-label {
  font-size: 11px;
  opacity: 0.7;
  font-weight: 400;
}

/* Logout Section */
.logout-section {
  margin: 20px;
  margin-top: 8px;
}

.logout-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  border-radius: 12px;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.2);
}

.logout-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.3);
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
}

.logout-btn:active {
  transform: translateY(0);
}

.logout-btn .nav-icon {
  font-size: 16px;
}

.logout-btn .nav-text {
  flex: 1;
  text-align: left;
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
    transition: transform 0.3s ease-in-out;
    z-index: 2000;
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  }

  .sidebar-mobile-open {
    transform: translateX(0);
  }

  .sidebar::before {
    content: '';
    position: fixed;
    top: 0;
    left: 280px;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0;
    visibility: hidden;
    transition: all 0.3s ease-in-out;
  }

  .sidebar-mobile-open::before {
    opacity: 1;
    visibility: visible;
  }

  .nav-section-title {
    font-size: 10px;
  }

  .nav-link {
    padding: 14px 20px;
  }

  .nav-text {
    font-size: 14px;
  }

  .dropdown-menu {
    margin-left: 16px;
  }

  .dropdown-nav-link {
    padding: 8px 12px;
    font-size: 12px;
  }

  .dropdown-nav-text {
    font-size: 12px;
  }

  .plan-section {
    margin: 16px;
    margin-bottom: 8px;
  }

  .plan-card {
    padding: 10px 12px;
  }

  .plan-name {
    font-size: 13px;
  }

  .plan-label {
    font-size: 10px;
  }

  .logout-section {
    margin: 16px;
    margin-top: 8px;
  }
}

/* Subscription Expired Styles */
.subscription-expired-overlay {
  margin: 16px;
  padding: 20px 16px;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  border: 1px solid #fca5a5;
  border-radius: 12px;
  text-align: center;
  color: #991b1b;
}

.expired-icon {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  color: #dc2626;
}

.expired-title {
  font-size: 15px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #7f1d1d;
}

.expired-message {
  font-size: 12px;
  margin: 0 0 14px;
  color: #991b1b;
  line-height: 1.5;
}

.renew-btn {
  width: 100%;
  padding: 10px 16px;
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(220, 38, 38, 0.3);
}

.renew-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.4);
}

.nav-disabled {
  pointer-events: none;
  opacity: 0.35;
  user-select: none;
}

/* ── Sidebar toggle button ───────────────────────────────────────── */
.sidebar-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  flex-shrink: 0;
}

.sidebar-toggle-btn:hover {
  background: var(--bg-primary);
  color: var(--text-primary);
}

/* Make the header a flex row so toggle sits right of logo */
.sidebar-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.sidebar-header-content {
  display: flex;
  flex-direction: column;
}

/* ── Collapsed sidebar ───────────────────────────────────────────── */
.sidebar-collapsed {
  width: 64px;
  transition: width 0.3s ease;
}

/* Allow tooltip to escape the nav container */
.sidebar-collapsed .sidebar-nav {
  overflow: visible;
}

.sidebar-collapsed .nav-text,
.sidebar-collapsed .nav-badge,
.sidebar-collapsed .nav-section-title,
.sidebar-collapsed .plan-info,
.sidebar-collapsed .logo-icon,
.sidebar-collapsed .dropdown-menu,
.sidebar-collapsed .dropdown-arrow,
.sidebar-collapsed .restricted-message {
  display: none;
}

.sidebar-collapsed .logo {
  justify-content: center;
}

.sidebar-collapsed .sidebar-header {
  justify-content: center;
  padding: 16px 10px;
}

.sidebar-collapsed .sidebar-toggle-btn {
  margin: 0 auto;
}

.sidebar-collapsed .nav-link,
.sidebar-collapsed .nav-link.dropdown-trigger {
  padding: 12px;
  justify-content: center;
}

.sidebar-collapsed .nav-icon {
  width: auto;
}

.sidebar-collapsed .nav-section {
  margin-bottom: 8px;
}

.sidebar-collapsed .plan-section {
  margin: 8px;
}

.sidebar-collapsed .plan-card {
  justify-content: center;
  padding: 10px;
}

.sidebar-collapsed .logout-section {
  margin: 8px;
}

.sidebar-collapsed .logout-btn {
  padding: 12px;
  justify-content: center;
}

/* Tooltip for collapsed items */
.sidebar-collapsed .nav-link {
  position: relative;
}

.sidebar-collapsed .nav-link::after {
  content: attr(data-tooltip);
  position: absolute;
  left: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 2000;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

.sidebar-collapsed .nav-link:hover::after {
  opacity: 1;
}

/* Normal sidebar transition */
.sidebar {
  transition: width 0.3s ease, background-color 0.3s ease;
}

/* ── Mobile: override collapsed styles so the sidebar opens full-width ── */
@media (max-width: 768px) {
  .sidebar-collapsed {
    width: 280px;
  }

  .sidebar-collapsed .nav-text {
    display: inline;
  }

  .sidebar-collapsed .nav-section-title {
    display: block;
  }

  .sidebar-collapsed .nav-badge {
    display: flex;
  }

  .sidebar-collapsed .logo-icon {
    display: block;
  }

  .sidebar-collapsed .plan-info {
    display: flex;
  }

  .sidebar-collapsed .dropdown-arrow {
    display: inline;
  }

  .sidebar-collapsed .nav-icon {
    width: auto;
  }

  .sidebar-collapsed .nav-link,
  .sidebar-collapsed .nav-link.dropdown-trigger {
    padding: 14px 20px;
    justify-content: flex-start;
  }

  .sidebar-collapsed .logo {
    justify-content: flex-start;
  }

  .sidebar-collapsed .sidebar-header {
    justify-content: space-between;
    padding: 24px 20px;
  }

  .sidebar-collapsed .sidebar-toggle-btn {
    display: none;
  }

  .sidebar-collapsed .nav-section {
    margin-bottom: 32px;
  }

  .sidebar-collapsed .plan-section {
    margin: 16px;
    margin-bottom: 8px;
  }

  .sidebar-collapsed .plan-card {
    justify-content: flex-start;
    padding: 10px 12px;
  }

  .sidebar-collapsed .logout-section {
    margin: 16px;
    margin-top: 8px;
  }

  .sidebar-collapsed .logout-btn {
    padding: 16px 20px;
    justify-content: flex-start;
  }

  /* Suppress tooltip-style ::after on mobile */
  .sidebar-collapsed .nav-link::after {
    display: none;
  }
}
</style>
